import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpError } from 'app/helpers/httpError';
import { HttpRequestHelper } from 'app/helpers/httpRequestHelper';
import { ExposedCreateInvoicePensionIncreaseReq } from 'app/interfaces/requests/Exposed/exposedCreateInvoicePensionIncreaseReq';
import { ExposedDivisionOfRulingDetailsReq } from 'app/interfaces/requests/Exposed/exposedDivisionOfRulingDetailsReq';
import { ExposedDoctorsCertificatePDFReq } from 'app/interfaces/requests/Exposed/exposedDoctorsCertificatePDFReq';
import { ExposedDocumentPensionIncreaseClaimsReq } from 'app/interfaces/requests/Exposed/exposedDocumentPensionIncreaseClaimsReq';
import { ExposedListDivisionOfRulingsLSRAReq } from 'app/interfaces/requests/Exposed/exposedListDivisionOfRulingsLSRAReq';
import { ExposedListPensionIncreaseClaimsReq } from 'app/interfaces/requests/Exposed/exposedListPensionIncreaseClaimsReq';
import { ExposedPensionBankDetailsPDFReq } from 'app/interfaces/requests/Exposed/exposedPensionBankDetailsPDFReq';
import { ExposedPensionSummaryReq } from 'app/interfaces/requests/Exposed/exposedPensionSummaryReq';
import { ExposedUpdateDivisionOfRulingsReq } from 'app/interfaces/requests/Exposed/exposedUpdateDivisionOfRulingsReq';
import { ExposedDivisionOfRulingDetailsRes } from 'app/interfaces/responses/Exposed/exposedDivisionOfRulingDetailsRes';
import { ExposedDivisionOfRulingsConfigsRes } from 'app/interfaces/responses/Exposed/exposedDivisionOfRulingsConfigsRes';
import { ExposedGeneratedPdfRes } from 'app/interfaces/responses/Exposed/exposedGeneratedPdfRes';
import { ExposedListDivisionOfRulingsLSRARes } from 'app/interfaces/responses/Exposed/exposedListDivisionOfRulingsLSRARes';
import { ExposedListPensionBenefitAcquisitionCoefficientRes } from 'app/interfaces/responses/Exposed/exposedListPensionBenefitAcquisitionCoefficientRes';
import { ExposedListPensionBenefitPayoutCoefficientRes } from 'app/interfaces/responses/Exposed/exposedListPensionBenefitPayoutCoefficientRes';
import { ExposedListKriaPensionIncreaseClaimsRes } from 'app/interfaces/responses/Exposed/exposedListPensionIncreaseClaimsRes';
import { ExposedPaycheckRunsRes } from 'app/interfaces/responses/Exposed/exposedPaycheckRunsRes';
import { exposedPostalShareLinkRes } from 'app/interfaces/responses/Exposed/exposedPostalShareLinkRes';
import { ExposedReadDocumentRes } from 'app/interfaces/responses/Exposed/exposedReadDocumentRes';
import { ExposedStateResponsibilityGroupAllEntriesExcelRes } from 'app/interfaces/responses/Exposed/exposedStateResponsibilityGroupAllEntriesExcelRes';
import { ExposedStateResponsibilityGroupChargeEntriesCheckIfLoadingRes } from 'app/interfaces/responses/Exposed/exposedStateResponsibilityGroupChargeEntriesCheckIfLoadingRes';
import { ExposedStateResponsibilityGroupChargedEntriesExcelRes } from 'app/interfaces/responses/Exposed/exposedStateResponsibilityGroupChargedEntriesExcelRes';
import { ExposedStateResponsibilityGroupPrepareEntriesCheckIfLoadingRes } from 'app/interfaces/responses/Exposed/exposedStateResponsibilityGroupPrepareEntriesCheckIfLoadingRes';
import { ExposedStateResponsibilityGroupPrepareEntriesExcelRes } from 'app/interfaces/responses/Exposed/exposedStateResponsibilityGroupPrepareEntriesExcelRes';
import { ListPendingTaxUpdateRes } from 'app/interfaces/responses/List/listPendingTaxUpdateRes';
import { PagedEntriesRes } from 'app/interfaces/responses/List/pagedEntriesRes';
import { HttpStatus } from 'app/interfaces/support/status/httpStatus';
import { AlertsService } from 'app/services/system/alerts/alerts.service';
import { LoaderService } from 'app/services/system/loader/loader.service';
import { environment } from 'environments/environment';
import { Observable, Subject } from 'rxjs';
import { TranslationService } from '../translation/translation.service';

@Injectable({
    providedIn: 'root',
})
export class PensionService {
    private httpRequestHelper = new HttpRequestHelper();
    constructor(
        private http: HttpClient,
        private alertsService: AlertsService,
        private router: Router,
        private loaderService: LoaderService,
        private translationService: TranslationService
    ) {}

    public getPensionBenefitAcquisitionCoefficients(): Promise<ExposedListPensionBenefitAcquisitionCoefficientRes[]> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage(this.translationService.translate('info_fetching_information'));

        return new Promise((resolve, reject) => {
            this.http
                .get(environment.localApi + 'pension/acquisitionCoefficients/')
                .subscribe({
                    next: (res: HttpStatus<ExposedListPensionBenefitAcquisitionCoefficientRes[]>) => resolve(res.returnItem),
                    error: (error) => {
                        HttpError(this.alertsService, this.router, error, '', '', '', '');
                        reject();
                    },
                })
                .add(() => {
                    this.loaderService.changeActiveLoaders(-1);
                });
        });
    }

    public updatePensionBenefitAcquisitionCoefficients(showError: boolean = true): Observable<null> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage(this.translationService.translatePlural('info_update_acquisition_coefficient'));

        let subject = new Subject<null>();
        this.http
            .post(environment.localApi + 'pension/updateAcquisitionCoefficients/', null, this.httpRequestHelper.getRequestOptionsAuth())
            .subscribe({
                next: (res: HttpStatus<null>) => {
                    this.alertsService.changeAlertShowIcon(true);
                    this.alertsService.changeAlertSuccessMessage(
                        this.translationService.translatePlural('info_update_coefficient_success')
                    );
                    this.alertsService.changeAlertSuccessShow(true);
                    subject.next(null);
                },
                error: (error) => {
                    if (showError) {
                        HttpError(this.alertsService, this.router, error, '', '', '', '');
                    } else {
                        console.log(error);
                    }
                },
            })
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });
        return subject.asObservable();
    }

    public getPensionBenefitPayoutCoefficients(): Promise<ExposedListPensionBenefitPayoutCoefficientRes[]> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage(this.translationService.translate('info_fetching_information'));
        return new Promise((resolve, reject) => {
            this.http
                .get(environment.localApi + 'pension/payoutCoefficients/')
                .subscribe({
                    next: (res: HttpStatus<ExposedListPensionBenefitPayoutCoefficientRes[]>) => resolve(res.returnItem),
                    error: (error) => {
                        HttpError(this.alertsService, this.router, error, '', '', '', '');
                        reject();
                    },
                })
                .add(() => {
                    this.loaderService.changeActiveLoaders(-1);
                });
        });
    }

    public calculatePensionBenefitPayoutCoefficients(
        req: ExposedListPensionBenefitPayoutCoefficientRes[],
        showLoader: boolean = false
    ): Promise<ExposedListPensionBenefitPayoutCoefficientRes[]> {
        if (showLoader) {
            this.loaderService.changeActiveLoaders(1);
            this.loaderService.changeLoaderMessage(this.translationService.translate('info_fetching_information'));
        }
        return new Promise((resolve, reject) => {
            this.http
                .post(environment.localApi + 'pension/calculatePensionBenefitPayoutCoefficients/', req)
                .subscribe({
                    next: (res: HttpStatus<ExposedListPensionBenefitPayoutCoefficientRes[]>) => resolve(res.returnItem),
                    error: (error) => {
                        HttpError(this.alertsService, this.router, error, '', '', '', '');
                        reject();
                    },
                })
                .add(() => {
                    showLoader && this.loaderService.changeActiveLoaders(-1);
                });
        });
    }

    public pensionSummaryA_deildPDF(req: ExposedPensionSummaryReq, showError = true): Observable<ExposedReadDocumentRes> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage('Undirbý skjal');

        // Make sure optional parameters works as should in LSR's API
        let _req: any = {
            ssn: req.ssn,
            paymentFromDate: req.paymentFromDate,
            paymentToDate: req.paymentToDate,
            letterMail: req.letterMail,
            showSalaryDate: req.showSalaryDate,
        };
        if (req.salaryFromDate) {
            _req.salaryFromDate = req.salaryFromDate;
        }
        if (req.salaryToDate) {
            _req.salaryToDate = req.salaryToDate;
        }

        let subject = new Subject<ExposedReadDocumentRes>();
        this.http
            .post(environment.localApi + 'pension/PensionSummaryA_deildPDF', _req, this.httpRequestHelper.getRequestOptionsAuth())
            .subscribe(
                (res: HttpStatus<ExposedReadDocumentRes>) => {
                    subject.next(res.returnItem);
                },
                (error) => {
                    if (showError) {
                        HttpError(this.alertsService, this.router, error, '', '', '', '');
                    } else {
                        console.log(error);
                    }
                }
            )
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });
        return subject.asObservable();
    }

    public pensionSummaryB_deildPDF(req: ExposedPensionSummaryReq, showError = true): Observable<ExposedReadDocumentRes> {
        if (req.salaryFromDate === undefined) {
            delete req.salaryFromDate;
        }
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage('Undirbý skjal');

        // Make sure optional parameters works as should in LSR's API
        let _req: any = {
            ssn: req.ssn,
            paymentFromDate: req.paymentFromDate,
            paymentToDate: req.paymentToDate,
            letterMail: req.letterMail,
            showSalaryDate: req.showSalaryDate,
        };
        if (req.salaryFromDate) {
            _req.salaryFromDate = req.salaryFromDate;
        }
        if (req.salaryToDate) {
            _req.salaryToDate = req.salaryToDate;
        }

        let subject = new Subject<ExposedReadDocumentRes>();
        this.http
            .post(environment.localApi + 'pension/PensionSummaryB_deildPDF', _req, this.httpRequestHelper.getRequestOptionsAuth())
            .subscribe(
                (res: HttpStatus<ExposedReadDocumentRes>) => {
                    subject.next(res.returnItem);
                },
                (error) => {
                    if (showError) {
                        HttpError(this.alertsService, this.router, error, '', '', '', '');
                    } else {
                        console.log(error);
                    }
                }
            )
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });
        return subject.asObservable();
    }

    public pensionSummarySereignPDF(req: ExposedPensionSummaryReq, showError = true): Observable<ExposedReadDocumentRes> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage('Undirbý skjal');

        // Make sure optional parameters works as should in LSR's API
        let _req: any = {
            ssn: req.ssn,
            paymentFromDate: req.paymentFromDate,
            paymentToDate: req.paymentToDate,
            letterMail: req.letterMail,
            showSalaryDate: req.showSalaryDate,
        };
        if (req.salaryFromDate) {
            _req.salaryFromDate = req.salaryFromDate;
        }
        if (req.salaryToDate) {
            _req.salaryToDate = req.salaryToDate;
        }

        let subject = new Subject<ExposedReadDocumentRes>();
        this.http
            .post(environment.localApi + 'pension/PensionSummarySereignPDF', _req, this.httpRequestHelper.getRequestOptionsAuth())
            .subscribe(
                (res: HttpStatus<ExposedReadDocumentRes>) => {
                    subject.next(res.returnItem);
                },
                (error) => {
                    if (showError) {
                        HttpError(this.alertsService, this.router, error, '', '', '', '');
                    } else {
                        console.log(error);
                    }
                }
            )
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });
        return subject.asObservable();
    }

    public pensionSummaryRecipientPDF(req: ExposedPensionSummaryReq, showError = true): Observable<ExposedReadDocumentRes> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage('Undirbý skjal');

        // Make sure optional parameters works as should in LSR's API
        let _req: any = {
            ssn: req.ssn,
            paymentFromDate: req.paymentFromDate,
            paymentToDate: req.paymentToDate,
            letterMail: req.letterMail,
            showSalaryDate: req.showSalaryDate,
        };
        if (req.salaryFromDate) {
            _req.salaryFromDate = req.salaryFromDate;
        }
        if (req.salaryToDate) {
            _req.salaryToDate = req.salaryToDate;
        }

        let subject = new Subject<ExposedReadDocumentRes>();
        this.http
            .post(environment.localApi + 'pension/PensionSummaryRecipientPDF', _req, this.httpRequestHelper.getRequestOptionsAuth())
            .subscribe(
                (res: HttpStatus<ExposedReadDocumentRes>) => {
                    subject.next(res.returnItem);
                },
                (error) => {
                    if (showError) {
                        HttpError(this.alertsService, this.router, error, '', '', '', '');
                    } else {
                        console.log(error);
                    }
                }
            )
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });
        return subject.asObservable();
    }

    public bankDetailsRequestPDF(req: ExposedPensionBankDetailsPDFReq, showError = true): Observable<ExposedReadDocumentRes> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage('Undirbý skjal');

        let subject = new Subject<ExposedReadDocumentRes>();
        this.http
            .post(environment.localApi + 'pension/BankDetailsRequestPDF', req, this.httpRequestHelper.getRequestOptionsAuth())
            .subscribe(
                (res: HttpStatus<ExposedReadDocumentRes>) => {
                    subject.next(res.returnItem);
                },
                (error) => {
                    if (showError) {
                        HttpError(this.alertsService, this.router, error, '', '', '', '');
                    } else {
                        console.log(error);
                    }
                }
            )
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });
        return subject.asObservable();
    }

    public doctorsCertificatePDF(req: ExposedDoctorsCertificatePDFReq, showError = true): Observable<ExposedReadDocumentRes> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage('Undirbý skjal');

        let subject = new Subject<ExposedReadDocumentRes>();
        this.http
            .post(environment.localApi + 'pension/DoctorsCertificatePDF', req, this.httpRequestHelper.getRequestOptionsAuth())
            .subscribe(
                (res: HttpStatus<ExposedReadDocumentRes>) => {
                    subject.next(res.returnItem);
                },
                (error) => {
                    if (showError) {
                        HttpError(this.alertsService, this.router, error, '', '', '', '');
                    } else {
                        console.log(error);
                    }
                }
            )
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });
        return subject.asObservable();
    }

    public stateResponsibilityGroupPrepareEntries(chargeRatio: number): Promise<number> {
        return new Promise((resolve, reject) => {
            this.http
                .get<HttpStatus<number>>(`${environment.localApi}pension/stateResponsibilityGroup/prepareEntries/${chargeRatio}`)
                .subscribe({
                    next: (res: HttpStatus<number>) => resolve(res.returnItem),
                    error: (error) => {
                        HttpError(this.alertsService, this.router, error, '', '', '', '');
                        reject();
                    },
                });
        });
    }

    public stateResponsibilityGroupPrepareCreateExcel(): Promise<number> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage(this.translationService.translate('info_create_document_one_two_min'));

        return new Promise<number>((resolve, reject) => {
            this.http
                .get(environment.localApi + `pension/stateResponsibilityGroup/prepareEntries/createExcel`)
                .subscribe({
                    next: (res: HttpStatus<number>) => {
                        resolve(res.returnItem);
                    },
                    error: (error) => {
                        HttpError(this.alertsService, this.router, error, '', '', '', '');
                        reject(error);
                    },
                })
                .add(() => {
                    this.loaderService.changeActiveLoaders(-1);
                });
        });
    }

    public stateResponsibilityGroupPrepareFetchExcel(jobId: number): Promise<ExposedStateResponsibilityGroupPrepareEntriesExcelRes> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage(this.translationService.translate('info_create_document_one_two_min'));

        return new Promise<ExposedStateResponsibilityGroupPrepareEntriesExcelRes>((resolve, reject) => {
            this.http
                .get(environment.localApi + `pension/stateResponsibilityGroup/prepareEntries/fetchExcel/` + jobId)
                .subscribe({
                    next: (res: HttpStatus<ExposedStateResponsibilityGroupPrepareEntriesExcelRes>) => {
                        resolve(res.returnItem);
                    },
                    error: (error) => {
                        HttpError(this.alertsService, this.router, error, '', '', '', '');
                        reject(error);
                    },
                })
                .add(() => {
                    this.loaderService.changeActiveLoaders(-1);
                });
        });
    }

    public stateResponsibilityGroupChargedCreateExcel(): Promise<number> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage(this.translationService.translate('info_create_document_one_two_min'));

        return new Promise<number>((resolve, reject) => {
            this.http
                .get(environment.localApi + `pension/stateResponsibilityGroup/chargedEntries/createExcel`)
                .subscribe({
                    next: (res: HttpStatus<number>) => {
                        resolve(res.returnItem);
                    },
                    error: (error) => {
                        HttpError(this.alertsService, this.router, error, '', '', '', '');
                        reject(error);
                    },
                })
                .add(() => {
                    this.loaderService.changeActiveLoaders(-1);
                });
        });
    }

    public stateResponsibilityGroupChargedFetchExcel(jobId: number): Promise<ExposedStateResponsibilityGroupChargedEntriesExcelRes> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage(this.translationService.translate('info_create_document_one_two_min'));

        return new Promise<ExposedStateResponsibilityGroupChargedEntriesExcelRes>((resolve, reject) => {
            this.http
                .get(environment.localApi + `pension/stateResponsibilityGroup/chargedEntries/fetchExcel/` + jobId)
                .subscribe({
                    next: (res: HttpStatus<ExposedStateResponsibilityGroupChargedEntriesExcelRes>) => {
                        resolve(res.returnItem);
                    },
                    error: (error) => {
                        HttpError(this.alertsService, this.router, error, '', '', '', '');
                        reject(error);
                    },
                })
                .add(() => {
                    this.loaderService.changeActiveLoaders(-1);
                });
        });
    }

    public stateResponsibilityGroupAllCreateExcel(): Promise<number> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage(this.translationService.translate('info_create_document_one_two_min'));

        return new Promise<number>((resolve, reject) => {
            this.http
                .get(environment.localApi + `pension/stateResponsibilityGroup/allEntries/createExcel`)
                .subscribe({
                    next: (res: HttpStatus<number>) => {
                        resolve(res.returnItem);
                    },
                    error: (error) => {
                        HttpError(this.alertsService, this.router, error, '', '', '', '');
                        reject(error);
                    },
                })
                .add(() => {
                    this.loaderService.changeActiveLoaders(-1);
                });
        });
    }

    public stateResponsibilityGroupAllFetchExcel(jobId: number): Promise<ExposedStateResponsibilityGroupAllEntriesExcelRes> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage(this.translationService.translate('info_create_document_one_two_min'));

        return new Promise<ExposedStateResponsibilityGroupAllEntriesExcelRes>((resolve, reject) => {
            this.http
                .get(environment.localApi + `pension/stateResponsibilityGroup/allEntries/fetchExcel/` + jobId)
                .subscribe({
                    next: (res: HttpStatus<ExposedStateResponsibilityGroupAllEntriesExcelRes>) => {
                        resolve(res.returnItem);
                    },
                    error: (error) => {
                        HttpError(this.alertsService, this.router, error, '', '', '', '');
                        reject(error);
                    },
                })
                .add(() => {
                    this.loaderService.changeActiveLoaders(-1);
                });
        });
    }

    public stateResponsibilityGroupPrepareEntriesCheckIfLoading(): Promise<ExposedStateResponsibilityGroupPrepareEntriesCheckIfLoadingRes> {
        return new Promise((resolve, reject) => {
            this.http
                .get<
                    HttpStatus<ExposedStateResponsibilityGroupPrepareEntriesCheckIfLoadingRes>
                >(`${environment.localApi}pension/stateResponsibilityGroup/prepareEntries/checkIfLoading`)
                .subscribe({
                    next: (res: HttpStatus<ExposedStateResponsibilityGroupPrepareEntriesCheckIfLoadingRes>) => resolve(res.returnItem),
                    error: (error) => {
                        HttpError(this.alertsService, this.router, error, '', '', '', '');
                        reject();
                    },
                });
        });
    }

    public stateResponsibilityGroupChargeEntriesCheckIfLoading(
        showError: boolean = true
    ): Promise<ExposedStateResponsibilityGroupChargeEntriesCheckIfLoadingRes> {
        return new Promise((resolve, reject) => {
            this.http
                .get<
                    HttpStatus<ExposedStateResponsibilityGroupChargeEntriesCheckIfLoadingRes>
                >(`${environment.localApi}pension/stateResponsibilityGroup/chargeEntries/checkIfLoading`)
                .subscribe({
                    next: (res: HttpStatus<ExposedStateResponsibilityGroupChargeEntriesCheckIfLoadingRes>) => resolve(res.returnItem),
                    error: (error) => {
                        if (showError) {
                            HttpError(this.alertsService, this.router, error, '', '', '', '');
                            reject();
                        } else {
                            console.log(error);
                        }
                    },
                });
        });
    }

    public stateResponsibilityGroupChargeEntries(): Promise<number> {
        return new Promise((resolve, reject) => {
            this.http.get<HttpStatus<number>>(`${environment.localApi}pension/stateResponsibilityGroup/chargeEntries`).subscribe({
                next: (res: HttpStatus<number>) => resolve(res.returnItem),
                error: (error) => {
                    HttpError(this.alertsService, this.router, error, '', '', '', '');
                    reject();
                },
            });
        });
    }

    public listPensionIncreaseClaims(req: ExposedListPensionIncreaseClaimsReq): Promise<Array<ExposedListKriaPensionIncreaseClaimsRes>> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage(this.translationService.translate('info_fetching_information'));

        return new Promise((resolve, reject) => {
            this.http
                .post(environment.localApi + 'pension/pensionIncreaseClaims/list', req)
                .subscribe({
                    next: (res: HttpStatus<Array<ExposedListKriaPensionIncreaseClaimsRes>>) => resolve(res.returnItem),
                    error: (error) => {
                        HttpError(this.alertsService, this.router, error, '', '', '', '');
                        reject();
                    },
                })
                .add(() => {
                    this.loaderService.changeActiveLoaders(-1);
                });
        });
    }

    public documentPensionIncreaseClaims(req: ExposedDocumentPensionIncreaseClaimsReq): Promise<ExposedReadDocumentRes> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage(this.translationService.translate('info_fetching_information'));

        return new Promise((resolve, reject) => {
            this.http
                .post(environment.localApi + 'pension/pensionIncreaseClaims/document', req)
                .subscribe({
                    next: (res: HttpStatus<ExposedReadDocumentRes>) => resolve(res.returnItem),
                    error: (error) => {
                        HttpError(this.alertsService, this.router, error, '', '', '', '');
                        reject();
                    },
                })
                .add(() => {
                    this.loaderService.changeActiveLoaders(-1);
                });
        });
    }

    public createInvoicePensionIncrease(req: ExposedCreateInvoicePensionIncreaseReq): Promise<boolean> {
        return new Promise((resolve, reject) => {
            this.http.post(environment.localApi + 'pension/pensionIncreaseClaims/createInvoice', req).subscribe({
                next: (res: HttpStatus<object>) => resolve(true),
                error: (error) => {
                    HttpError(this.alertsService, this.router, error, '', '', '', '');
                    reject(error.error);
                },
            });
        });
    }

    public getPdfPensionPaycheck(changeLoaderMessage: boolean = true, paycheckNumber: string | null): Promise<ExposedGeneratedPdfRes> {
        this.loaderService.changeActiveLoaders(1);
        if (changeLoaderMessage) {
            this.loaderService.changeLoaderMessage(this.translationService.translate('info_fetching_information'));
        }

        return new Promise((resolve, reject) => {
            this.http
                .get<HttpStatus<ExposedGeneratedPdfRes>>(environment.localApi + 'pension/paychecks/PdfPensionPaycheck/' + paycheckNumber)
                .subscribe({
                    next: (res: HttpStatus<ExposedGeneratedPdfRes>) => resolve(res.returnItem),
                    error: (error) => {
                        HttpError(this.alertsService, this.router, error, '', '', '', '');
                        reject();
                    },
                })
                .add(() => {
                    this.loaderService.changeActiveLoaders(-1);
                });
        });
    }

    public getDivisionOfRulingsConfigs(showError = true): Observable<ExposedDivisionOfRulingsConfigsRes> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage('Sæki gögn');
        let subject = new Subject<ExposedDivisionOfRulingsConfigsRes>();
        this.http
            .get(environment.localApi + 'pension/getDivisionOfRulingsConfigs', this.httpRequestHelper.getRequestOptionsAuth())
            .subscribe(
                (res: HttpStatus<ExposedDivisionOfRulingsConfigsRes>) => {
                    subject.next(res.returnItem);
                },
                (error) => {
                    if (showError) {
                        HttpError(this.alertsService, this.router, error, '', '', '', '');
                    } else {
                        console.log(error);
                    }
                }
            )
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });
        return subject.asObservable();
    }

    public getDivisionOfRulingsLSRA(
        req: ExposedListDivisionOfRulingsLSRAReq,
        showError = true
    ): Observable<Array<ExposedListDivisionOfRulingsLSRARes>> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage('Sæki gögn');
        let subject = new Subject<Array<ExposedListDivisionOfRulingsLSRARes>>();
        this.http
            .post(environment.localApi + 'pension/getDivisionOfRulingsLSRA', req, this.httpRequestHelper.getRequestOptionsAuth())
            .subscribe(
                (res: HttpStatus<Array<ExposedListDivisionOfRulingsLSRARes>>) => {
                    subject.next(res.returnItem);
                },
                (error) => {
                    if (showError) {
                        HttpError(this.alertsService, this.router, error, '', '', '', '');
                    } else {
                        console.log(error);
                    }
                }
            )
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });
        return subject.asObservable();
    }

    public getDivisionOfRulingDetails(
        req: ExposedDivisionOfRulingDetailsReq,
        showError = true
    ): Observable<ExposedDivisionOfRulingDetailsRes> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage('Sæki gögn');
        let subject = new Subject<ExposedDivisionOfRulingDetailsRes>();
        this.http
            .post(environment.localApi + 'pension/getDivisionOfRulingDetails', req, this.httpRequestHelper.getRequestOptionsAuth())
            .subscribe(
                (res: HttpStatus<ExposedDivisionOfRulingDetailsRes>) => {
                    subject.next(res.returnItem);
                },
                (error) => {
                    if (showError) {
                        HttpError(this.alertsService, this.router, error, '', '', '', '');
                    } else {
                        console.log(error);
                    }
                }
            )
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });
        return subject.asObservable();
    }

    public updateDivisionOfRulings(req: ExposedUpdateDivisionOfRulingsReq, showError: boolean = true): Observable<null> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage(this.translationService.translatePlural('info_update_division_of_rulings'));

        let subject = new Subject<null>();
        this.http
            .post(environment.localApi + 'pension/updateDivisionOfRulings/', req, this.httpRequestHelper.getRequestOptionsAuth())
            .subscribe({
                next: (res: HttpStatus<null>) => {
                    this.alertsService.changeAlertShowIcon(true);
                    this.alertsService.changeAlertSuccessMessage(
                        this.translationService.translatePlural('info_update_division_of_rulings_success')
                    );
                    this.alertsService.changeAlertSuccessShow(true);
                    subject.next(null);
                },
                error: (error) => {
                    if (showError) {
                        this.alertsService.changealertHeader(error.error.errorItem.friendlyMessage);
                        this.alertsService.changeAlertErrorMessage(error.error.errorItem.message);
                        this.alertsService.changeAlertErrorShow(true);
                    } else {
                        console.log(error);
                    }
                },
            })
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });
        return subject.asObservable();
    }

    public getPaycheckRuns(
        pageNumber: number = 0,
        pageSize: number = 25,
        searchString: string = '',
        dateFrom: Date | string,
        dateTo: Date | string,
        changeLoaderMessage: boolean = true
    ): Promise<PagedEntriesRes<ExposedPaycheckRunsRes>> {
        this.loaderService.changeActiveLoaders(1);

        if (changeLoaderMessage) {
            this.loaderService.changeLoaderMessage(this.translationService.translate('info_fetching_information'));
        }

        let params = new HttpParams().set('pageNumber', pageNumber.toString()).set('pageSize', pageSize.toString());

        if (searchString?.trim()) params = params.set('searchString', searchString.trim());

        if (dateFrom) params = params.set('dateFrom', new Date(dateFrom).toISOString());
        if (dateTo) params = params.set('dateTo', new Date(dateTo).toISOString());

        return new Promise((resolve, reject) => {
            this.http
                .get<HttpStatus<PagedEntriesRes<ExposedPaycheckRunsRes>>>(environment.localApi + 'pension/paychecks/runs', { params })
                .subscribe({
                    next: (res) =>
                        resolve(
                            res.returnItem ??
                                ({ items: [], totalCount: 0, pageNumber, pageSize } as PagedEntriesRes<ExposedPaycheckRunsRes>)
                        ),
                    error: (error) => {
                        HttpError(this.alertsService, this.router, error, '', '', '', '');
                        reject();
                    },
                })
                .add(() => this.loaderService.changeActiveLoaders(-1));
        });
    }

    public createPostalShareLink(paycheckRunId: number, maxCount = 500): Promise<exposedPostalShareLinkRes> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage(this.translationService.translate('info_fetching_information'));

        return new Promise((resolve, reject) => {
            this.http
                .post<HttpStatus<exposedPostalShareLinkRes>>(
                    environment.localApi + `pension/paychecks/runs/${paycheckRunId}/postal/share-link`,
                    { maxCount }
                )
                .subscribe({
                    next: (res) => resolve(res.returnItem),
                    error: (err) => {
                        HttpError(this.alertsService, this.router, err, '', '', '', '');
                        reject(err);
                    },
                })
                .add(() => this.loaderService.changeActiveLoaders(-1));
        });
    }

    public getPostalPdfByTokenResponse(token: string): Promise<HttpResponse<Blob>> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage(this.translationService.translate('info_fetching_information'));

        return new Promise((resolve, reject) => {
            const params = new HttpParams().set('token', token);

            this.http
                .get(environment.localApi + `pension/paychecks/runs/postal/pdf-by-token`, {
                    params,
                    responseType: 'blob',
                    observe: 'response',
                })
                .subscribe({
                    next: (res) => resolve(res),
                    error: (err) => {
                        HttpError(this.alertsService, this.router, err, '', '', '', '');
                        reject(err);
                    },
                })
                .add(() => this.loaderService.changeActiveLoaders(-1));
        });
    }

    public getPendingTaxUpdates(ssn: string = ''): Observable<HttpStatus<ListPendingTaxUpdateRes[]>> {
        const normalizedSsn = ssn.replace(/\D/g, '').slice(0, 10);
        let params = new HttpParams();
        if (normalizedSsn) {
            params = params.set('ssn', normalizedSsn);
        }

        return this.http.get<HttpStatus<ListPendingTaxUpdateRes[]>>(environment.localApi + 'pension/pendingTaxUpdates', {
            ...this.httpRequestHelper.getRequestOptionsAuth(),
            params,
        });
    }
}
